import React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { PRIORITY } from 'wix-ui-tpa/Button';

import withGroups from '../../contexts/withGroups';
import {
  withAppSettings,
  WithAppSettingsProps,
} from '../../contexts/withAppSettings';
import withGroupsActions, {
  WithGroupsActionsProps,
} from '../../contexts/withGroupsActions';

import { st, classes } from './Groups.st.css';
import {
  AppToasts,
  InjectedWebOutProps,
  withAppToasts,
  withWebOut,
} from '@wix/social-groups-common/dist/src/components';
import { withTPAConfig } from '@wix/social-groups-common/dist/src/components/withTPAConfig';
import { WithAppToastsProps } from '@wix/social-groups-common/dist/src/types';
import { compose } from '@wix/social-groups-common/dist/src/compose';
import { Text, TYPOGRAPHY } from 'wix-ui-tpa/Text';
import {
  canCreateGroup,
  canResolvePendingGroups,
} from '@wix/social-groups-api';
import { ApiTypes } from '@wix/social-groups-api/dist/src/types';

import { Button } from '../Button/Button';
import { SearchSortingBox } from '../SearchSortingBox/SearchSortingBox';
import { DATA_HOOKS } from './dataHooks';
import {
  withCurrentUser,
  WithCurrentUserProps,
} from '../../contexts/withCurrentUser';
import { PendingGroupList } from '../PendingGroupList';
import {
  BIUserEntry,
  MemberInvites,
  tryToCallBi,
  withBiLogger,
  WithBiLoggerProps,
  withMemberInvites,
  withWixSiteMembers,
  WixSiteMember,
  WixSiteMemberActions,
} from '@wix/social-groups-common/dist/src/context';
import { WithGroupsProps } from '../../contexts/withGroupsProps';
import { GroupsSectionComponent } from './GroupsSection';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';
import { getSettingsKeyFor } from '@wix/social-groups-common/dist/src/utils/utils';

const isApprovalStatusPending = (group) =>
  group.approvalStatus === ApiTypes.v1.GroupApprovalStatus.PENDING;

export interface GroupsProps {}

export interface GroupsState {
  isCreateGroupModalOpened: boolean;
  isGroupCreating: boolean;
  isProfileDialogOpen: boolean;
}

export type ComponentProps = InjectedTranslateProps &
  GroupsProps &
  WithCurrentUserProps &
  WithGroupsProps &
  WithGroupsActionsProps &
  WithAppToastsProps &
  WixSiteMember &
  WixSiteMemberActions &
  WithAppSettingsProps & { mobile: boolean } & InjectedWebOutProps &
  MemberInvites &
  InjectedExperimentsProps &
  WithBiLoggerProps;

export class GroupsComponent extends GroupsSectionComponent {
  static getDerivedStateFromProps(nextProps, prevState): Partial<GroupsState> {
    const { isGroupCreating } = nextProps;
    const nextState: Partial<GroupsState> = {
      isGroupCreating,
    };
    if (isGroupCreating === false && prevState.isGroupCreating === true) {
      nextState.isCreateGroupModalOpened = false;
    }

    return nextState;
  }

  render() {
    const {
      actions,
      createGroupPolicy,
      currentUser,
      groupsSortBy,
      appSettings,
      groups,
      t,
      toasts,
      mobile,
      groupSectionUrl,
    } = this.props;

    const withTitle = appSettings[getSettingsKeyFor('showHeaderTitle', mobile)];
    const withSearchSortingBox =
      appSettings[getSettingsKeyFor('showSearchSorting', mobile)];

    const shouldShowCreateGroupButton = canCreateGroup(
      currentUser,
      createGroupPolicy,
    );

    const couldUserResolvePendingGroups = canResolvePendingGroups(currentUser);
    const shouldShowPendingGroupList =
      groups && groups.some(isApprovalStatusPending);

    const { fonts } = appSettings.styleParams;
    const headerTag = fonts.listTitleFont && fonts.listTitleFont.htmlTag;

    return (
      <div data-hook="groups" className={st(classes.root, { mobile })}>
        <AppToasts t={t} {...toasts} />
        <div className={classes.header}>
          <Text
            data-hook={DATA_HOOKS.groupListTitle}
            tagName={headerTag || 'h1'}
            typography={TYPOGRAPHY.largeTitle}
            className={st(classes.title, {
              headerAlignment: appSettings.headerAlignment,
            })}
          >
            {withTitle &&
              (appSettings.groupListTitle == null ? t('groups-web.settings.TextTab.groups.default') : appSettings.groupListTitle)
            }
          </Text>
          {/* TODO: refactor for widget (+ performance) */}
          {shouldShowCreateGroupButton && (
            <Button
              priority={PRIORITY.secondary}
              data-hook={DATA_HOOKS.createNewButton}
              onClick={this.handleCreateGroupClick}
              className={classes.createNewButton}
            >
              {t('groups-web.btn.create-new')}
            </Button>
          )}
        </div>
        {shouldShowPendingGroupList && (
          <PendingGroupList
            data-hook={DATA_HOOKS.pendingGroupList}
            couldUserResolvePendingGroups={couldUserResolvePendingGroups}
            actions={actions}
            groups={groups}
            mobile={mobile}
            groupSectionUrl={groupSectionUrl}
          />
        )}
        {withSearchSortingBox && (
          <SearchSortingBox
            onSearch={this.handleSearch}
            onSort={actions.sortGroups}
            initialSortSelectedId={groupsSortBy}
          />
        )}
        {super.render()}
      </div>
    );
  }

  private readonly handleSearch = (value: string) => {
    const { actions, biLogger } = this.props;
    tryToCallBi(async () => {
      await biLogger.groupsSearchForGroup({
        group_name: value,
        origin: 'top_panel_editbox',
        userEntry: BIUserEntry.SITE,
      });
    });
    actions.searchGroups(value);
  };
}

const enhance = compose(
  withGroups,
  withCurrentUser,
  withGroupsActions,
  withAppToasts,
  withWixSiteMembers,
  withAppSettings,
  translate(),
  withTPAConfig,
  withWebOut,
  withMemberInvites,
  withBiLogger,
  withExperiments,
);
export const Groups = enhance(
  GroupsComponent,
) as React.ComponentType<GroupsProps>;
